/* The map — "the map is the application" (UI-UX-DESIGN.md Principle 1).
 *
 * ── One renderer, two projections ──────────────────────────────────────────
 *
 * MapLibre GL 5.x has native globe projection and native 3D terrain, so 2D and 3D
 * are the SAME renderer over the SAME style and the SAME state. That satisfies
 * AD-08 literally rather than by discipline: there is no second scene graph to
 * diverge from the first.
 *
 * The docs originally specified CesiumJS for 3D. MapLibre globe supersedes it for
 * the operator view; Cesium remains a candidate later for glTF platform models and
 * precise orbital visualisation, which MapLibre does not do. See ARCHITECTURE.md AD-08.
 *
 * Projection switching is animated by `setProjection` interpolating mercator↔globe,
 * which preserves the operator's spatial bearings (UX §4.1).
 */
import { useEffect, useRef, useState } from 'react'
import maplibregl, { type Map as MlMap } from 'maplibre-gl'
import { AOI } from '../data/geo'
import { useDeck } from '../state/store'
import { ATTRIBUTION, BASEMAPS, MAP_MODE, buildStyle, styleHasDem } from '../lib/basemaps'
import type { BasemapId } from '../lib/basemaps'
import { EMPTY, sensorEnvelopes, threatEnvelopes, weaponEnvelopes } from '../lib/envelopes'
import { trails } from '../lib/trails'
import { SymbolOverlay } from './SymbolOverlay'
import { MeasureReadout } from './MeasureReadout'
import { DemoScript } from './DemoScript'
import { CoursePlotter } from './CoursePlotter'
import { planCourse, resolveCourseTarget } from '../lib/course'
import { RefPointsPanel } from './RefPointsPanel'
import { MissionEditor } from './MissionEditor'
import { OrbatPanel } from './OrbatPanel'
import { InteropPanel } from './InteropPanel'
import { LossesPanel } from './LossesPanel'
import { ScenarioPanel } from './ScenarioPanel'
import { AnalysisPanel } from './AnalysisPanel'
import { ZONE_COLOR } from '../data/refpoints'

/** Above ~zoom 3 MapLibre's globe is visually flat. */
const GLOBE_ZOOM = 2.1

export function MapCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const styleReadyRef = useRef(false)
  const applyingStyleRef = useRef(false)
  const pendingStyleRef = useRef<{ basemap: BasemapId; globe: boolean } | null>(null)
  const debounceRef = useRef(0)
  // Children (overlay, measure, demo) need the instance, so it goes in state as
  // well as a ref — a ref alone would not re-render them when the map appears.
  const [mapReady, setMapReady] = useState<MlMap | null>(null)

  const entities = useDeck((s) => s.entities)
  const contacts = useDeck((s) => s.contacts)
  const layers = useDeck((s) => s.layers)
  const select = useDeck((s) => s.select)
  const basemap = useDeck((s) => s.basemap)
  const bundle = useDeck((s) => s.bundle)
  const viewMode = useDeck((s) => s.viewMode)
  const terrain3d = useDeck((s) => s.terrain3d)
  const exaggeration = useDeck((s) => s.terrainExaggeration)
  const tool = useDeck((s) => s.tool)
  const measure = useDeck((s) => s.measure)
  const measurePick = useDeck((s) => s.measurePick)
  const courses = useDeck((s) => s.courses)
  const courseFocus = useDeck((s) => s.courseFocus)
  const courseAdd = useDeck((s) => s.courseAdd)
  const selection = useDeck((s) => s.selection)
  const refPoints = useDeck((s) => s.refPoints)
  const zones = useDeck((s) => s.zones)
  const refPointAdd = useDeck((s) => s.refPointAdd)
  const refPointMove = useDeck((s) => s.refPointMove)
  const focusTarget = useDeck((s) => s.focusTarget)
  const clearFocus = useDeck((s) => s.clearFocus)

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(basemap, viewMode === '3d'),
      center: AOI.center,
      zoom: AOI.zoom,
      maxPitch: 85, // needed for meaningful 3D terrain viewing
      attributionControl: false,
    })

    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'bottom-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'nautical' }), 'bottom-right')
    map.addControl(new maplibregl.GlobeControl(), 'bottom-right')

    map.on('style.load', () => {
      styleReadyRef.current = true
    })

    mapRef.current = map
    setMapReady(map)

    // Test handle. Read-only reference to the map so end-to-end tests can project
    // geographic coordinates to screen space instead of guessing pixel positions —
    // a guessed click silently tests nothing. The client holds no secrets, so
    // exposing the instance costs nothing.
    ;(window as unknown as { __orbis?: { map: MlMap } }).__orbis = { map }

    // MapLibre measures its container once at construction. Inside a flex column that
    // height is not settled yet, and it never re-measures on its own. It also has to
    // survive the log dock expanding/collapsing, which changes the map's height.
    const ro = new ResizeObserver(() => map.resize())
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      map.remove()
      mapRef.current = null
      setMapReady(null)
      delete (window as unknown as { __orbis?: unknown }).__orbis
      styleReadyRef.current = false
    }
    // Init once. Basemap changes are handled by the setStyle effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Style: basemap AND projection are both style properties ────────────────
  //
  // Projection and sky live in the StyleSpecification, so one setStyle handles a
  // basemap change, a 2D/3D change, or both. Applying projection imperatively after
  // setStyle meant any basemap switch silently reverted the globe to mercator.
  //
  // Calls are COALESCED: setStyle while a previous style load is still in flight
  // aborts its tile requests, and MapLibre then throws "Cannot read properties of
  // undefined (reading 'signal')" on the cancelled request. The scripted demo hits
  // this every time it changes basemap and projection in quick succession. Only the
  // latest requested style is applied.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    pendingStyleRef.current = { basemap, globe: viewMode === '3d' }

    // DEBOUNCED as well as coalesced. Coalescing alone still issues one setStyle per change once
    // the previous settles, and MapLibre aborts in-flight sprite/glyph requests on every style
    // replacement — then dereferences the cancelled request and throws "Cannot read properties of
    // undefined (reading 'signal')". The guided tour changes basemap and projection within a second
    // of each other and reproduced it every run.
    //
    // A short debounce turns a burst into ONE style application, which removes the aborted requests
    // rather than merely surviving them. 220 ms is below the threshold at which a deliberate click
    // feels laggy and well above the gap a scripted sequence produces.
    const run = () => {
      const want = pendingStyleRef.current
      if (!want || !mapRef.current) return
      pendingStyleRef.current = null
      applyingStyleRef.current = true
      styleReadyRef.current = false
      // `diff: false`, deliberately, after three attempts at making the diff path behave.
      //
      // MapLibre's diff path leaves queued sprite/image requests attached to a style that no longer
      // exists, then dereferences their cancelled abort controller — "Cannot read properties of
      // undefined (reading 'signal')". Coalescing the calls and then debouncing them both reduced
      // the frequency without removing the cause, because the cause is not concurrency: it is the
      // diff leaving work pointing at a discarded style.
      //
      // A full rebuild tears down cleanly. It costs a tile re-fetch, so a basemap switch flashes
      // briefly — an acceptable price for a user-initiated, infrequent action, and it also makes
      // `style.load` fire reliably, which the diff path does not (that asymmetry was itself the
      // cause of an earlier silent bug where the store and the render disagreed about projection).
      map.setStyle(buildStyle(want.basemap, want.globe), { diff: false })

      // `style.load` does NOT fire when setStyle resolves via a diff rather than a
      // full rebuild. Waiting only on it leaves the in-flight flag stuck true and
      // every subsequent style change is silently dropped — which is how the demo
      // ended on the globe while the store said 2D. `idle` and a timeout are the
      // safety nets; whichever arrives first settles it.
      let done = false
      let timer = 0
      const settle = () => {
        if (done) return
        done = true
        map.off('style.load', settle)
        map.off('idle', settle)
        window.clearTimeout(timer)
        applyingStyleRef.current = false
        styleReadyRef.current = true
        if (pendingStyleRef.current) run()
      }
      map.on('style.load', settle)
      map.on('idle', settle)
      timer = window.setTimeout(settle, 1500)
    }

    if (applyingStyleRef.current) return
    window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(run, 220)
    return () => window.clearTimeout(debounceRef.current)
    // `bundle` is a dependency because in bundle mode the style is built FROM the manifest: the
    // map is created before the fetch resolves, so without this the first render is the fallback
    // and stays there until something else happens to change the basemap.
  }, [basemap, viewMode, bundle])

  // ── Camera framing for the projection ──────────────────────────────────────
  //
  // The zoom threshold is the whole trick. MapLibre's globe is only visibly
  // spherical below roughly zoom 3; above that it is indistinguishable from a flat
  // map, so "3D" reads as "the map got greyer". Pull back to GLOBE_ZOOM and keep
  // pitch at 0: a sphere seen head-on with space around its limb is unmistakable,
  // whereas a pitched near-view just looks like a tilted flat map.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (viewMode === '3d') {
      map.easeTo({ pitch: 0, bearing: 0, zoom: Math.min(map.getZoom(), GLOBE_ZOOM), duration: 1200 })
    } else {
      map.easeTo({ pitch: 0, bearing: 0, zoom: Math.max(map.getZoom(), AOI.zoom), duration: 900 })
    }
  }, [viewMode])

  // ── 3D terrain relief ──────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const apply = () => {
      const canDem = styleHasDem(basemap) && !!map.getSource('orbisDem')
      if (terrain3d && canDem) {
        map.setTerrain({ source: 'orbisDem', exaggeration })
      } else {
        map.setTerrain(null)
      }
      // Hillshade is separate from 3D terrain: it reads well in flat 2D, where
      // exaggerated relief would just distort the tactical picture.
      if (map.getLayer('orbis-hillshade')) {
        map.setLayoutProperty(
          'orbis-hillshade',
          'visibility',
          layers.terrain && !terrain3d ? 'visible' : 'none',
        )
      }
    }
    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [terrain3d, exaggeration, basemap, layers.terrain])

  // ── Layer visibility ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const apply = () => {
      const set = (id: string, on: boolean) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none')
      }
      set('orbis-graticule-minor', layers.graticule)
      set('orbis-graticule-major', layers.graticule)
      set('orbis-bathy', layers.bathymetry)
      for (const id of ['land-fill', 'land-line', 'landcover', 'landuse', 'park']) {
        set(id, layers.terrain)
      }
    }
    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [layers, basemap])

  // ── Uncertainty ellipses — a contact is an estimate, never a point of truth ─
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const data = {
        type: 'FeatureCollection' as const,
        features: layers.contacts
          ? contacts.map((c) => ({
              type: 'Feature' as const,
              properties: { r: c.uncertaintyNm, aff: c.affiliation },
              geometry: { type: 'Point' as const, coordinates: [c.lon, c.lat] },
            }))
          : [],
      }

      const existing = map.getSource('uncertainty') as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(data)
        return
      }

      map.addSource('uncertainty', { type: 'geojson', data })
      map.addLayer({
        id: 'uncertainty-ring',
        type: 'circle',
        source: 'uncertainty',
        paint: {
          // `interpolate` must be top-level; the per-feature radius multiplies inside
          // each stop. MapLibre rejects ["*", …, ["zoom"]].
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3, ['*', ['get', 'r'], 0.35],
            6, ['*', ['get', 'r'], 2.6],
            9, ['*', ['get', 'r'], 20],
          ],
          'circle-color': 'transparent',
          'circle-stroke-width': 1,
          'circle-stroke-color': [
            'match',
            ['get', 'aff'],
            '6', 'rgba(232,81,90,0.40)',
            '5', 'rgba(232,81,90,0.28)',
            '4', 'rgba(79,196,138,0.32)',
            'rgba(229,192,75,0.34)',
          ],
        },
      })
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [contacts, layers.contacts, basemap])

  // ── Envelopes and trails ───────────────────────────────────────────────────
  //
  // The highest-value layers in the product: "can I be seen?" and "can I shoot?".
  // v0.9 draws GEOMETRIC envelopes from the dataset's instrumented ranges. The engine
  // replaces them with terrain-masked, model-derived coverage in v1.0 — the UI says
  // "geometric" so a demo cannot imply more than is true.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const defs: { id: string; data: ReturnType<typeof sensorEnvelopes>; on: boolean }[] = [
        { id: 'env-sensor', data: layers.sensorRanges ? sensorEnvelopes(entities) : EMPTY, on: layers.sensorRanges },
        { id: 'env-weapon', data: layers.weaponEnvelopes ? weaponEnvelopes(entities) : EMPTY, on: layers.weaponEnvelopes },
        { id: 'env-threat', data: layers.threatEnvelopes ? threatEnvelopes(contacts) : EMPTY, on: layers.threatEnvelopes },
      ]

      for (const d of defs) {
        const src = map.getSource(d.id) as maplibregl.GeoJSONSource | undefined
        if (src) {
          src.setData(d.data)
          continue
        }
        map.addSource(d.id, { type: 'geojson', data: d.data })

        const stroke =
          d.id === 'env-threat'
            ? 'rgba(232,81,90,0.62)'
            : d.id === 'env-weapon'
              ? 'rgba(229,169,75,0.55)'
              : 'rgba(79,163,227,0.42)'
        const fill =
          d.id === 'env-threat'
            ? 'rgba(232,81,90,0.075)'
            : d.id === 'env-weapon'
              ? 'rgba(229,169,75,0.05)'
              : 'rgba(79,163,227,0.038)'

        map.addLayer({
          id: `${d.id}-fill`,
          type: 'fill',
          source: d.id,
          paint: { 'fill-color': fill },
        })
        map.addLayer({
          id: `${d.id}-line`,
          type: 'line',
          source: d.id,
          paint: {
            'line-color': stroke,
            'line-width': d.id === 'env-threat' ? 1.4 : 1,
            // A degraded sensor's reduced coverage is dashed, so "degraded" is legible
            // on the map rather than only in the detail panel.
            'line-dasharray': d.id === 'env-weapon' ? [4, 3] : [1, 0],
          },
        })
      }
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [entities, contacts, layers.sensorRanges, layers.weaponEnvelopes, layers.threatEnvelopes, basemap])

  // ── Track history trails ───────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const data = layers.tracks
        ? trails(entities, contacts, layers.units, layers.contacts)
        : { type: 'FeatureCollection' as const, features: [] }

      const src = map.getSource('trails') as maplibregl.GeoJSONSource | undefined
      if (src) {
        src.setData(data)
        return
      }
      map.addSource('trails', { type: 'geojson', data, lineMetrics: true })

      // One layer PER AFFILIATION. `line-gradient` replaces `line-color` entirely, so
      // a single layer could have the fade or the affiliation colour but not both —
      // and affiliation is safety-critical information, so it wins.
      const AFF: { key: string; match: string[]; rgb: string }[] = [
        { key: 'friend', match: ['3', '2'], rgb: '79,163,227' },
        { key: 'hostile', match: ['6', '5'], rgb: '232,81,90' },
        { key: 'neutral', match: ['4'], rgb: '79,196,138' },
        { key: 'unknown', match: ['1', '0'], rgb: '229,192,75' },
      ]

      for (const a of AFF) {
        map.addLayer({
          id: `trails-${a.key}`,
          type: 'line',
          source: 'trails',
          filter: ['in', ['get', 'affiliation'], ['literal', a.match]],
          layout: { 'line-cap': 'round' },
          paint: {
            'line-width': ['case', ['==', ['get', 'kind'], 'entity'], 1.6, 1.1],
            // Fades into the past: the head of the track is where the unit is NOW,
            // and the tail must not compete with it.
            'line-gradient': [
              'interpolate',
              ['linear'],
              ['line-progress'],
              0, `rgba(${a.rgb},0)`,
              0.6, `rgba(${a.rgb},0.22)`,
              1, `rgba(${a.rgb},0.75)`,
            ],
          },
        })
      }
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [entities, contacts, layers.tracks, layers.units, layers.contacts, basemap])

  // ── Reference points, mission areas, exclusion zones ───────────────────────
  //
  // Reference points that share an `area` close into a polygon. That indirection is
  // why they exist: move a point and the mission area moves with it.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const rpData = {
        type: 'FeatureCollection' as const,
        features: layers.refPoints
          ? refPoints.map((r) => ({
              type: 'Feature' as const,
              id: r.id,
              properties: { id: r.id, name: r.name },
              geometry: { type: 'Point' as const, coordinates: [r.lon, r.lat] },
            }))
          : [],
      }

      // Group reference points by area, in declaration order, and close the ring.
      const areas = new Map<string, [number, number][]>()
      if (layers.missions) {
        for (const r of refPoints) {
          if (!r.area) continue
          const ring = areas.get(r.area) ?? []
          ring.push([r.lon, r.lat])
          areas.set(r.area, ring)
        }
      }
      const areaData = {
        type: 'FeatureCollection' as const,
        features: [...areas.entries()]
          .filter(([, ring]) => ring.length >= 3)
          .map(([name, ring]) => ({
            type: 'Feature' as const,
            properties: { name },
            geometry: { type: 'Polygon' as const, coordinates: [[...ring, ring[0]]] },
          })),
      }

      const zoneData = {
        type: 'FeatureCollection' as const,
        features: layers.zones
          ? zones.map((z) => ({
              type: 'Feature' as const,
              properties: { name: z.name, kind: z.kind },
              geometry: { type: 'Polygon' as const, coordinates: [z.ring] },
            }))
          : [],
      }

      const existing = map.getSource('refpoints') as maplibregl.GeoJSONSource | undefined
      if (existing) {
        existing.setData(rpData)
        ;(map.getSource('mission-areas') as maplibregl.GeoJSONSource).setData(areaData)
        ;(map.getSource('zones') as maplibregl.GeoJSONSource).setData(zoneData)
        return
      }

      map.addSource('zones', { type: 'geojson', data: zoneData })
      map.addSource('mission-areas', { type: 'geojson', data: areaData })
      map.addSource('refpoints', { type: 'geojson', data: rpData })

      map.addLayer({
        id: 'zones-fill',
        type: 'fill',
        source: 'zones',
        paint: {
          'fill-color': [
            'match', ['get', 'kind'],
            'keepout', ZONE_COLOR.keepout.fill,
            'no_fly', ZONE_COLOR.no_fly.fill,
            'weapons_free', ZONE_COLOR.weapons_free.fill,
            ZONE_COLOR.weapons_tight.fill,
          ],
        },
      })
      map.addLayer({
        id: 'zones-line',
        type: 'line',
        source: 'zones',
        paint: {
          'line-color': [
            'match', ['get', 'kind'],
            'keepout', ZONE_COLOR.keepout.line,
            'no_fly', ZONE_COLOR.no_fly.line,
            'weapons_free', ZONE_COLOR.weapons_free.line,
            ZONE_COLOR.weapons_tight.line,
          ],
          'line-width': 1.3,
          'line-dasharray': [5, 3],
        },
      })
      map.addLayer({
        id: 'mission-area-fill',
        type: 'fill',
        source: 'mission-areas',
        paint: { 'fill-color': 'rgba(96,165,250,0.055)' },
      })
      map.addLayer({
        id: 'mission-area-line',
        type: 'line',
        source: 'mission-areas',
        paint: { 'line-color': 'rgba(96,165,250,0.5)', 'line-width': 1.1, 'line-dasharray': [2, 2] },
      })
      // Invisible hit target. The visible dot is 3.5px, which is far below any
      // usable pointer target — dragging it would be a coin flip. Picking happens
      // against this layer instead.
      map.addLayer({
        id: 'refpoint-hit',
        type: 'circle',
        source: 'refpoints',
        paint: { 'circle-radius': 12, 'circle-color': 'rgba(0,0,0,0.001)' },
      })
      map.addLayer({
        id: 'refpoint-dot',
        type: 'circle',
        source: 'refpoints',
        paint: {
          'circle-radius': 3.5,
          'circle-color': '#0a0f1a',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': 'rgba(160,190,225,0.85)',
        },
      })
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [refPoints, zones, layers.refPoints, layers.missions, layers.zones, basemap])

  // ── Drag a reference point ─────────────────────────────────────────────────
  //
  // Keyed on `mapReady` (state), not `mapRef.current`. A ref does not re-trigger the
  // effect, so whether the handlers ever got registered depended on effect ordering
  // relative to map construction — and they silently did not. State makes the
  // dependency explicit and the registration guaranteed.
  useEffect(() => {
    const map = mapReady
    if (!map) return
    let dragging: string | null = null

    const down = (e: maplibregl.MapMouseEvent) => {
      if (!map.getLayer('refpoint-hit')) return
      const hit = map.queryRenderedFeatures(e.point, { layers: ['refpoint-hit'] })[0]
      if (!hit) return
      dragging = hit.properties?.id as string
      // Suppress map pan for the duration of the drag, or the point and the map
      // both move and the point appears stuck.
      map.dragPan.disable()
      e.preventDefault()
    }
    const move = (e: maplibregl.MapMouseEvent) => {
      if (!dragging) return
      refPointMove(dragging, { lon: e.lngLat.lng, lat: e.lngLat.lat })
    }
    const up = () => {
      if (!dragging) return
      dragging = null
      map.dragPan.enable()
    }

    const hover = (e: maplibregl.MapMouseEvent) => {
      if (dragging || !map.getLayer('refpoint-hit')) return
      const over = map.queryRenderedFeatures(e.point, { layers: ['refpoint-hit'] }).length > 0
      const canvas = map.getCanvas()
      // Only override the cursor when no tool has claimed it.
      if (over) canvas.style.cursor = 'grab'
      else if (canvas.style.cursor === 'grab') canvas.style.cursor = ''
    }

    map.on('mousedown', down)
    map.on('mousemove', move)
    map.on('mousemove', hover)
    map.on('mouseup', up)
    return () => {
      map.off('mousedown', down)
      map.off('mousemove', move)
      map.off('mousemove', hover)
      map.off('mouseup', up)
    }
  }, [mapReady, refPointMove])

  // ── Plotted courses: legs + waypoints, for EVERY unit that has one ─────────
  //
  // Legs past bingo render red, so an unachievable plan is obvious on the MAP and not
  // only in the panel — the operator is looking at the map while drawing.
  //
  // All courses draw at once, with the unit being worked on at full strength and the rest
  // dimmed. Drawing only the selected unit's course was the original behaviour and it made a
  // multi-unit plan impossible to see, let alone compare — the whole reason to plot two
  // courses is to look at them together.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const active = resolveCourseTarget(selection, courses, courseFocus).entityId
      const legFeatures: GeoJSON.Feature[] = []
      const wpFeatures: GeoJSON.Feature[] = []

      // Sorted so feature order — and therefore draw order — does not depend on the order
      // the operator happened to plot in.
      for (const entityId of Object.keys(courses).sort()) {
        const waypoints = courses[entityId]
        const entity = entities.find((e) => e.id === entityId)
        if (!entity || !waypoints.length) continue

        const isActive = entityId === active
        const plan = planCourse(entity, waypoints)
        for (const l of plan.legs) {
          legFeatures.push({
            type: 'Feature',
            properties: { reachable: l.reachable, index: l.index, active: isActive, entityId },
            geometry: {
              type: 'LineString',
              coordinates: [
                [l.from.lon, l.from.lat],
                [l.to.lon, l.to.lat],
              ],
            },
          })
          wpFeatures.push({
            type: 'Feature',
            properties: {
              label: String(l.index + 1),
              reachable: l.reachable,
              active: isActive,
              // Only the active course labels its waypoints. Numbering every course at once
              // produces overlapping "1"s wherever two plans cross, which reads as noise.
              designation: entity.designation,
            },
            geometry: { type: 'Point', coordinates: [l.to.lon, l.to.lat] },
          })
        }
      }

      const legData = { type: 'FeatureCollection' as const, features: legFeatures }
      const wpData = { type: 'FeatureCollection' as const, features: wpFeatures }

      const legSrc = map.getSource('course-legs') as maplibregl.GeoJSONSource | undefined
      if (legSrc) {
        legSrc.setData(legData)
        ;(map.getSource('course-wp') as maplibregl.GeoJSONSource).setData(wpData)
        return
      }

      map.addSource('course-legs', { type: 'geojson', data: legData })
      map.addSource('course-wp', { type: 'geojson', data: wpData })

      map.addLayer({
        id: 'course-line',
        type: 'line',
        source: 'course-legs',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': ['case', ['get', 'reachable'], '#60a5fa', '#e8515a'],
          // The unit being worked on reads as the foreground one; the others stay legible
          // enough to compare against without competing for attention.
          'line-width': ['case', ['get', 'active'], 2.2, 1.3],
          'line-opacity': ['case', ['get', 'active'], 1, 0.45],
          'line-dasharray': ['case', ['get', 'reachable'], ['literal', [1, 0]], ['literal', [3, 2]]],
        },
      })
      map.addLayer({
        id: 'course-wp-dot',
        type: 'circle',
        source: 'course-wp',
        paint: {
          'circle-radius': ['case', ['get', 'active'], 4.5, 3],
          'circle-color': '#0a0f1a',
          'circle-stroke-width': 1.6,
          'circle-stroke-color': ['case', ['get', 'reachable'], '#60a5fa', '#e8515a'],
          'circle-opacity': ['case', ['get', 'active'], 1, 0.5],
          'circle-stroke-opacity': ['case', ['get', 'active'], 1, 0.5],
        },
      })
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [courses, courseFocus, selection, entities, basemap])

  // ── Measure tool line ──────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const apply = () => {
      const coords =
        measure.from && measure.to
          ? [
              [measure.from.lon, measure.from.lat],
              [measure.to.lon, measure.to.lat],
            ]
          : []
      const data = {
        type: 'FeatureCollection' as const,
        features: coords.length
          ? [
              {
                type: 'Feature' as const,
                properties: {},
                geometry: { type: 'LineString' as const, coordinates: coords },
              },
            ]
          : [],
      }
      const src = map.getSource('measure') as maplibregl.GeoJSONSource | undefined
      if (src) {
        src.setData(data)
        return
      }
      map.addSource('measure', { type: 'geojson', data })
      map.addLayer({
        id: 'measure-line',
        type: 'line',
        source: 'measure',
        paint: {
          'line-color': '#60a5fa',
          'line-width': 1.4,
          'line-dasharray': [3, 2],
        },
      })
    }

    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
  }, [measure, basemap])

  // NOTE: an effect here used to CLEAR the plotted course whenever the operator selected a
  // different unit — which is what made a second unit's plot destroy the first one's. Courses
  // are per entity now, so switching unit simply changes which one is in the foreground and
  // there is nothing to abandon. Deleting an operator's work as a side effect of a selection
  // change was never right.

  // ── Camera focus requests from panels ──────────────────────────────────────
  // Panels ask for a camera move via state rather than holding the map instance.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !focusTarget) return
    map.flyTo({
      center: [focusTarget.lon, focusTarget.lat],
      zoom: focusTarget.zoom ?? map.getZoom(),
      duration: 1000,
    })
    clearFocus()
  }, [focusTarget, clearFocus])

  // ── Map click: measure pick, or deselect ───────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (tool === 'measure') {
        measurePick({ lon: e.lngLat.lng, lat: e.lngLat.lat })
        return
      }
      if (tool === 'refpoint') {
        refPointAdd({ lon: e.lngLat.lng, lat: e.lngLat.lat })
        return
      }
      if (tool === 'course') {
        // Selection wins over the course in progress — see resolveCourseTarget.
        const { entityId } = resolveCourseTarget(selection, courses, courseFocus)
        if (entityId) courseAdd(entityId, { lon: e.lngLat.lng, lat: e.lngLat.lat })
        return
      }
      select(null)
    }
    map.on('click', onClick)
    return () => {
      map.off('click', onClick)
    }
  }, [select, tool, measurePick, courses, courseFocus, selection, courseAdd, refPointAdd])

  // Crosshair while measuring — the cursor should say what the tool does.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.getCanvas().style.cursor = tool === 'measure' || tool === 'course' || tool === 'refpoint' ? 'crosshair' : ''
  }, [tool])

  const def = BASEMAPS.find((b) => b.id === basemap)
  const attribution = ATTRIBUTION[basemap]

  return (
    <div className="relative h-full w-full">
      {/* Sized with h-full, NOT `absolute inset-0`: MapLibre overwrites the container's
          `position` to `relative` on init, which cancels absolute positioning and
          collapses the element to 0 height. */}
      <div ref={containerRef} className="h-full w-full" />

      <SymbolOverlay map={mapReady} />
      <MeasureReadout />
      <CoursePlotter />
      <RefPointsPanel />
      <MissionEditor />
      <OrbatPanel />
      <LossesPanel />
      <InteropPanel />
      <ScenarioPanel />
      <AnalysisPanel />
      <DemoScript map={mapReady} />

      {/* Attribution + provenance. Licence obligations are satisfied on screen, and
          the tile mode is stated so a live session is never mistaken for air-gapped. */}
      <div className="pointer-events-none absolute bottom-1.5 left-3 z-10 flex items-center gap-2"
        style={{ paddingLeft: 82 }}>
        <span
          className="label rounded-sm px-1 py-px"
          style={{
            fontSize: 8.5,
            color: MAP_MODE === 'live' ? 'var(--color-warn)' : 'var(--color-ok)',
            background: 'rgba(7,13,24,0.7)',
            border: `1px solid ${MAP_MODE === 'live' ? 'rgba(229,169,75,0.3)' : 'rgba(79,196,138,0.3)'}`,
          }}
          title={
            MAP_MODE === 'live'
              ? 'Live upstream tiles — DEVELOPMENT ONLY. Production uses a staged Atlas bundle (CON-01).'
              : 'Staged Atlas bundle — no network access.'
          }
        >
          {MAP_MODE === 'live' ? 'LIVE TILES · DEV' : 'BUNDLE · OFFLINE'}
        </span>
        <span className="label" style={{ fontSize: 8.5, color: 'var(--color-text-3)' }}>
          {def?.label} — {attribution}
        </span>
        {(layers.sensorRanges || layers.weaponEnvelopes || layers.threatEnvelopes) && (
          <span
            className="label rounded-sm px-1 py-px"
            style={{
              fontSize: 8.5,
              color: 'var(--color-text-3)',
              background: 'rgba(7,13,24,0.7)',
              border: '1px solid var(--color-line-2)',
            }}
            title="v0.9: envelopes are geodesic geometry at the dataset's instrumented range. Not terrain-masked, not model-derived — the engine produces real coverage in v1.0."
          >
            ENVELOPES: GEOMETRIC
          </span>
        )}
      </div>
    </div>
  )
}
